import React, { useEffect } from 'react';
import { GetServerSideProps } from 'next';
import HeadContent from '../../../../components/HeadContent';
import BasePage, {
  SchedulerData,
  BasePageProps,
} from '../../../../components/BasePage';
import { getBasePageProps } from '../../../../lib/basePageProps';
import {
  errorPageViewEvent,
  referralPageViewEvent,
} from '../../../../lib/tracking/eventPayloadDefinitions';
import { trackEvent } from '../../../../lib/tracking';
import {
  EventParams,
  setCheckoutButtonHref,
  addCheckoutButtonTracking,
} from '../../../../lib/checkoutButton';
import { extractUserIdNoError } from '../../../../server/lib/userid';
import { getScheduledResource } from '../../../../server/api/scheduler';
import { getMarketConfig } from '../../../../server/api/country-attributes/country-attributes';
import { getUserCountry } from '../../../../server/api/useraccount';
// CountryTracking is the user's registration country (geolocation is the fallback)
// Market is the user's geolocation
export type PageProps = {
  basePageProps: BasePageProps;
  schedulerData: SchedulerData;
  market: string;
  countryTracking: string;
  code: string;
  isAuthenticated: boolean;
  errorCode?: string | null;
};

const RECEIVER_PAGE = 'referrals:receiver';

export const getServerSideProps: GetServerSideProps<
  PageProps,
  { market: string; code: string }
> = async ({ req, res, params }) => {
  let pageName = RECEIVER_PAGE;

  const userId = extractUserIdNoError(req);
  const marketGeolocation = params!.market;
  let registrationCountry = null;

  if (userId) {
    registrationCountry = await getUserCountry(userId);
  }

  const { country, system_language: locale } = await getMarketConfig({
    spotifyMarket: marketGeolocation,
  });

  const [basePageData, schedulerResponse] = await Promise.allSettled([
    getBasePageProps({
      spotifyMarket: marketGeolocation,
      userId: extractUserIdNoError(req),
    }),
    getScheduledResource({
      localisation: {
        translationLocales: [locale],
        currencyLocale: locale,
        dateLocale: locale,
      },
      platform: 'WEB',
      resource: pageName,
      targeting: {
        country: registrationCountry ? registrationCountry : country,
      },
    }),
  ]);

  let schedulerData;

  if (basePageData.status === 'rejected') {
    res.statusCode = 500;
    throw basePageData.reason;
  }

  const sanitizedBasePageProps = JSON.parse(
    JSON.stringify(basePageData.value, (key, value) => {
      if (key === 'scrollingElementAccessor') {
        return undefined;
      }
      return value;
    }),
  );

  if (schedulerResponse.status === 'rejected') {
    try {
      pageName = 'referrals:error:country-invalid';

      schedulerData = await getScheduledResource({
        localisation: {
          translationLocales: [locale],
          currencyLocale: locale,
          dateLocale: locale,
        },
        platform: 'WEB',
        resource: pageName,
        targeting: {
          country: registrationCountry ? registrationCountry : country,
        },
      });

      schedulerData = schedulerData.content;
    } catch (e) {
      res.statusCode = 500;
      throw e;
    }
  } else {
    schedulerData = schedulerResponse.value.content;
  }

  const isAuthenticated = !!req.headers['sp-internal-user-id'];

  const errorCode = pageName === RECEIVER_PAGE ? null : pageName;

  return {
    props: {
      basePageProps: sanitizedBasePageProps,
      schedulerData: schedulerData,
      market: marketGeolocation,
      countryTracking: registrationCountry ? registrationCountry : country,
      code: params!.code,
      isAuthenticated,
      errorCode,
    },
  };
};

const ReferralPage = ({
  basePageProps,
  schedulerData,
  market,
  countryTracking,
  code,
  isAuthenticated,
  errorCode,
}: PageProps) => {
  useEffect(() => {
    if (errorCode) {
      trackEvent(
        errorPageViewEvent(
          {
            page: errorCode,
            market: countryTracking,
          },
          { authorize: isAuthenticated },
        ),
      );
    } else {
      trackEvent(
        referralPageViewEvent(
          {
            page: 'referrals:receiver',
            market: countryTracking,
            http_referer: document.referrer,
          },
          { authorize: isAuthenticated },
        ),
      );
    }
  });

  const enrichSchedulerData = (simpleSchedulerData: SchedulerData) => {
    const makeCheckoutLink = (eventParams: EventParams) =>
      `/${market}/referral/${code}/continue/${eventParams.campaignId}-${eventParams.offerTypeId}`;

    const [enrichedSchedulerData] = [simpleSchedulerData]
      .map(data => setCheckoutButtonHref(data, makeCheckoutLink))
      .map(data =>
        addCheckoutButtonTracking(data, countryTracking, code, {
          authorize: isAuthenticated,
        }),
      );

    return enrichedSchedulerData;
  };

  return (
    <>
      <HeadContent {...schedulerData.metadata} />
      <BasePage
        basePageProps={basePageProps}
        schedulerData={
          errorCode ? schedulerData : enrichSchedulerData(schedulerData)
        }
      />
    </>
  );
};

export default ReferralPage;
